import { Announcement02, Grid01 } from "@untitledui/icons";
import { toDisplayName } from "@/pages/connect-ads/advertiser-picker";
import { ConnectionRow } from "./connection-row";
import { useTikTokSellerConnections } from "./use-tiktok-seller-connections";

export const ADS_GUIDE_TOOLTIP =
  "When you authorize, tick every field under “Access Your Ad Account For”. They are what let us read your ad performance and exclude or remove videos from your TikTok Ads campaigns.";

const ADS_MISSING_SCOPES_MESSAGE =
  "Some of the permissions we need are turned off. Authorise again and tick every field under “Access Your Ad Account For”, or we can't read your ad performance or manage which videos run in your campaigns.";

const ADS_MISSING_ADVERTISER_MESSAGE =
  "Authorise again and confirm which advertiser account we should manage, so we only act on the right campaigns.";

const ADS_CONNECTED_MESSAGE = "See the permissions you have connected in TikTok";

const ADS_MISSING_SCOPES_LABEL = "We're missing some permissions";

const ADS_MISSING_ADVERTISER_LABEL = "Confirm your advertiser account";

/**
 * The two TikTok Shop/Ads connection rows — shared between TikTokSellerConnectionsModal
 * and the Integrations page so both stay in sync. The reconnect callout is rendered by
 * each consumer separately since it needs to sit outside this list in their layouts.
 */
export const PermissionsList = ({ enabled, highlightAds }: { enabled: boolean; highlightAds?: boolean }) => {
  const {
    shopState,
    isShopLoading,
    shopAuthUrl,
    handleShopConnect,
    adsStatus,
    adsState,
    isAdsLoading,
    adsAuthUrl,
    adsManageUrl,
    handleAdsConnect,
  } = useTikTokSellerConnections(enabled);

  const adsAccount = adsStatus?.advertiserId
    ? {
        advertiserId: adsStatus.advertiserId,
        name: adsStatus.advertiserName
          ? toDisplayName({
              advertiserId: adsStatus.advertiserId,
              name: adsStatus.advertiserName,
              country: null,
              status: null,
            })
          : null,
        region: adsStatus.advertiserRegion,
      }
    : null;

  return (
    <>
      <ConnectionRow
        icon={Grid01}
        title="TikTok Shop Access"
        description="Sync products, video performance and creator messages for reporting and management in Unbox."
        state={shopState}
        isLoading={isShopLoading}
        onConnect={handleShopConnect}
        disabled={!shopAuthUrl}
      />
      <hr className="border-0 border-t border-secondary" />
      <ConnectionRow
        icon={Announcement02}
        title="Advertising Data"
        description="Access ad performance data and manage which videos are eligible for ads."
        state={adsState}
        isLoading={isAdsLoading}
        onConnect={handleAdsConnect}
        disabled={!adsAuthUrl}
        guideTooltip={ADS_GUIDE_TOOLTIP}
        incompleteLabel={
          adsStatus?.reason === "advertiser_not_selected" ? ADS_MISSING_ADVERTISER_LABEL : ADS_MISSING_SCOPES_LABEL
        }
        incompleteMessage={
          adsStatus?.reason === "advertiser_not_selected" ? ADS_MISSING_ADVERTISER_MESSAGE : ADS_MISSING_SCOPES_MESSAGE
        }
        connectedMessage={ADS_CONNECTED_MESSAGE}
        manageUrl={adsManageUrl}
        account={adsAccount}
        stats={adsStatus?.stats ?? null}
        highlight={highlightAds}
      />
    </>
  );
};
